import { Box, Text, Flex, BoxProps, ElementProps } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useDiscussionParticipants } from "hooks/useDiscussionParticipants";
import { useParams } from "react-router";
import { IconChevronLeft, IconX } from "@tabler/icons-react";
import { motion } from "motion/react";
import UserAvatar from "components/UserAvatar";
import { useDiscussion } from "hooks/useDiscussions";
import { DiscussionType } from "@cnx/common";
import { useAuthStore } from "stores/authStore";
import { getUserName } from "components/Users/helpers";

const MBox = motion.create(Box<BoxProps & ElementProps<"div", keyof BoxProps>>);

interface ChatParticipantsProps {
  discussionId: string;
  onClose: () => void;
  onClosePanel: () => void;
}

export const ChatParticipants = ({
  discussionId: discussionIdProp,
  onClose,
  onClosePanel,
}: ChatParticipantsProps) => {
  const { t } = useTranslation("discussions");
  const currentUserId = useAuthStore(state => state.currentUserId);
  const { discussionId: discussionIdParam } = useParams<{
    discussionId: string;
  }>();
  const discussionId = discussionIdProp ?? discussionIdParam;
  const { participants } = useDiscussionParticipants({
    discussionId: discussionId!,
  });
  const { discussion } = useDiscussion({ discussionId: discussionId! });

  return (
    <Box className="bg-white-darker flex flex-col min-h-screen">
      <MBox
        className="bg-white w-full flex items-center justify-between sticky top-0 z-10 border-b border-gray"
        mih={66}
        component={"header"}
      >
        <Box className="flex items-center gap-2">
          {onClose ? (
            <button
              className="m-2 cursor-pointer"
              onClick={onClose}
              aria-label="Go back"
            >
              <IconChevronLeft
                height={24}
                width={24}
                color="var(--color-gray-dark)"
              />
            </button>
          ) : (
            <div className="m-1 shrink-0" />
          )}
        </Box>
        <Box className="flex flex-col items-start px-4 grow">
          <Text size="sm" fw={600}>
            {discussion?.discussionType === DiscussionType.PRIVATE
              ? t("contact")
              : t("groupMembers")}
          </Text>
        </Box>
        {onClosePanel && (
          <button
            onClick={onClosePanel}
            aria-label="Close"
            className="p-0 flex items-center text-gray-dark hover:text-black cursor-pointer mr-2"
          >
            <IconX size={32} />
          </button>
        )}
      </MBox>
      <Flex direction="column">
        {participants
          .filter(participant =>
            discussion?.discussionType === DiscussionType.PRIVATE
              ? participant.id !== currentUserId
              : true
          )
          .map(participant => (
            <Box
              key={participant.id}
              className="h-20 border-b border-gray bg-white pl-6"
            >
              <Flex align="center" gap="sm" h="100%">
                <UserAvatar
                  size={50}
                  userName={getUserName({ ...participant, initialsOnly: true })}
                  avatarId={participant.avatar ?? ""}
                />
                <Box>
                  <Text size="sm" fw={600}>
                    {getUserName(participant) || "-"}
                  </Text>
                  {participant.role && (
                    <Text size="xs" fw={400} className="text-gray-medium">
                      {participant.role}
                    </Text>
                  )}
                  <Text size="xs" fw={400} className="text-gray-medium">
                    {participant.email}
                  </Text>
                </Box>
              </Flex>
            </Box>
          ))}
      </Flex>
    </Box>
  );
};

export default ChatParticipants;
